import { ClockIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { compareVersionTags } from '../lib/version.ts';
import type {
  AppPipelineState,
  Deployment,
  DeploymentQueueItem,
  InfrastructureApp,
  PlanResult,
  VerificationResult,
} from '../types.ts';
import { AppEditDialog } from './AppEditDialog';
import { ChangesIndicator } from './ChangesIndicator';
import { DeploymentDialog } from './DeploymentDialog';
import { HistoryDialog } from './HistoryDialog';
import { PipelineArrow } from './PipelineArrow';
import { PlanDialog } from './PlanDialog';
import { ValidationDialog } from './ValidationDialog';

interface AppPipelineProps {
  pipeline: AppPipelineState;
  app: InfrastructureApp;
  devDeploymentId?: number;
  prodDeploymentId?: number;
  onValidate: (deploymentId: number) => Promise<void>;
  onUpdateApp: (
    appName: string,
    environments: string[],
    validationInstructions?: Record<string, { info?: string }>,
    bulkDeploymentExclusions?: string[],
  ) => Promise<void>;
  onEnqueueDeployment: (item: DeploymentQueueItem) => void;
  onDequeueDeployment: (appName: string, environment: string) => void;
  queuedDeployments: DeploymentQueueItem[];
  deployments: Deployment[];
  planResults: PlanResult[];
  verificationResults: VerificationResult[];
}

export const AppPipeline = ({
  pipeline,
  app,
  devDeploymentId,
  prodDeploymentId,
  onValidate,
  onUpdateApp,
  onEnqueueDeployment,
  onDequeueDeployment,
  queuedDeployments,
  deployments,
  planResults,
  verificationResults,
}: AppPipelineProps) => {
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [deploymentDialogOpen, setDeploymentDialogOpen] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedValidation, setSelectedValidation] = useState<{
    deploymentId: number;
    environment: string;
    version: string;
    deployedAt: string;
    validationMissing: boolean;
  } | null>(null);
  const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanResult | null>(null);

  const handleDeploymentClick = (deployment: Deployment | undefined) => {
    if (!deployment) {
      return;
    }
    setSelectedDeployment(deployment);
    setDeploymentDialogOpen(true);
  };

  const handlePlanClick = (plan: PlanResult | undefined) => {
    if (!plan) {
      return;
    }
    setSelectedPlan(plan);
    setPlanDialogOpen(true);
  };

  const handleValidationClick = (
    deploymentId: number | undefined,
    environment: string,
    version: string | undefined,
    deployedAt: string | undefined,
    validationMissing: boolean,
  ) => {
    if (deploymentId === undefined || !version || !deployedAt) {
      return;
    }
    setSelectedValidation({ deploymentId, environment, version, deployedAt, validationMissing });
    setValidationDialogOpen(true);
  };

  const handleValidate = async (deploymentId: number) => {
    await onValidate(deploymentId);
    setValidationDialogOpen(false);
  };

  const enabledEnvironments = app.environments
    .split(',')
    .map((environment) => environment.trim())
    .filter(Boolean);

  // Find deployment objects
  const devDeployment = deployments.find(
    (d) => d.appName === pipeline.appName && d.environment === 'dev',
  );
  const prodDeployment = deployments.find(
    (d) => d.appName === pipeline.appName && d.environment === 'prod',
  );
  const devValidationDeployment = deployments.find((d) => d.id === devDeploymentId);
  const prodValidationDeployment = deployments.find((d) => d.id === prodDeploymentId);

  // Find plan result for the deployed prod version
  const prodPlanForVersion = prodDeployment
    ? planResults.find(
        (p) =>
          p.appName === pipeline.appName &&
          p.environment === 'prod' &&
          p.version === prodDeployment.version,
      )
    : undefined;

  // Determine if validation steps are clickable (always clickable if deployment exists)
  const devValidationClickable = !!devDeploymentId;
  const prodValidationClickable = !!prodDeploymentId;

  // Determine if deployment steps are clickable (always clickable if deployment exists)
  const devDeploymentClickable = !!devDeployment;
  const prodDeploymentClickable = !!prodDeployment;
  const devEnabled = enabledEnvironments.includes('dev');

  const isQueued = (environment: string) =>
    queuedDeployments.some(
      (item) => item.appName === pipeline.appName && item.environment === environment,
    );

  const handleEnqueueDeployment = (environment: string) => {
    onEnqueueDeployment({
      appName: pipeline.appName,
      environment,
    });
  };

  const handleToggleDeployment = (environment: string) => {
    if (isQueued(environment)) {
      onDequeueDeployment(pipeline.appName, environment);
      return;
    }

    handleEnqueueDeployment(environment);
  };

  const getProdDeployReadiness = () => {
    if (!pipeline.prodDeployment.hasChanges) {
      return {
        recommended: false,
        title: 'No prod drift detected',
      };
    }

    if (!devEnabled) {
      return {
        recommended: true,
        title: 'Prod drift detected',
      };
    }

    if (!devDeployment) {
      return {
        recommended: false,
        title: 'Prod drift detected, but dev has not been deployed yet',
      };
    }

    if (devDeployment.status === 'pending' || devDeployment.status === 'in_progress') {
      return {
        recommended: false,
        title: 'Prod drift detected, but dev deployment is still running',
      };
    }

    if (devDeployment.status === 'failure' || devDeployment.status === 'error') {
      return {
        recommended: false,
        title: 'Prod drift detected, but dev deployment failed',
      };
    }

    const devProdComparison = prodDeployment
      ? compareVersionTags(devDeployment.version, prodDeployment.version)
      : undefined;

    if (prodDeployment && devProdComparison !== undefined && devProdComparison <= 0) {
      return {
        recommended: false,
        title: 'Prod drift detected, but dev is not ahead of prod',
      };
    }

    if (prodDeployment && devProdComparison === undefined) {
      return {
        recommended: false,
        title: 'Prod drift detected, but dev/prod versions could not be compared',
      };
    }

    if (pipeline.devValidation.status !== 'success') {
      return {
        recommended: false,
        title: 'Prod drift detected, but dev has not been validated yet',
      };
    }

    return {
      recommended: true,
      title: 'Prod drift detected and dev is ready for promotion',
    };
  };

  const renderDeployStep = (environment: 'dev' | 'prod', isFirst = false) => {
    const enabled = enabledEnvironments.includes(environment);
    const queued = isQueued(environment);
    const sensitive = app.bulkDeploymentExclusions.includes(environment);
    const prodReadiness = environment === 'prod' ? getProdDeployReadiness() : undefined;
    const readyColor = environment === 'prod' ? (prodReadiness?.recommended ?? true) : true;

    return (
      <PipelineArrow
        label={
          queued
            ? `${environment === 'prod' ? 'Prod' : 'Dev'} Queued`
            : `Deploy ${environment === 'prod' ? 'Prod' : 'Dev'}`
        }
        status={!enabled ? 'disabled' : queued ? 'pending' : readyColor ? 'success' : 'warning'}
        disabledReason={!enabled ? 'environment_disabled' : undefined}
        isFirst={isFirst}
        compact
        className={isFirst ? undefined : '-ml-3'}
        clickable={enabled}
        onClick={() => handleToggleDeployment(environment)}
        title={
          queued
            ? `Remove ${environment} deployment from queue`
            : (prodReadiness?.title ?? `Add ${environment} deployment to queue`)
        }
        warningTitle={
          sensitive ? 'Sensitive deployment: excluded from bulk deployment actions' : undefined
        }
      />
    );
  };

  return (
    <div className='rounded-lg pb-4'>
      {/* App name with history button */}
      <div className='mb-4 flex items-center justify-between'>
        <h3
          className='text-lg font-semibold text-primary hover:text-primary/80 cursor-pointer transition-colors'
          onClick={() => setEditDialogOpen(true)}
          title='Click to edit app configuration'
        >
          {pipeline.appName}
        </h3>
        <div className='flex items-center gap-2'>
          <button
            onClick={() => setHistoryDialogOpen(true)}
            className='px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors flex items-center gap-2 cursor-pointer'
            title='View deployment history'
          >
            <ClockIcon className='w-4 h-4' />
            <span>History</span>
          </button>
        </div>
      </div>

      {/* Pipeline visualization */}
      <div className='flex items-center gap-0 w-full'>
        {renderDeployStep('dev', true)}

        <PipelineArrow
          label='Dev Deployment'
          status={pipeline.devDeployment.status}
          version={pipeline.devDeployment.version}
          date={pipeline.devDeployment.date}
          disabledReason={pipeline.devDeployment.disabledReason}
          className='-ml-3'
          clickable={devDeploymentClickable}
          onClick={() => handleDeploymentClick(devDeployment)}
        />

        <PipelineArrow
          label='Dev Validation'
          status={pipeline.devValidation.status}
          version={pipeline.devValidation.version}
          date={pipeline.devValidation.date}
          disabledReason={pipeline.devValidation.disabledReason}
          className='-ml-3'
          clickable={devValidationClickable}
          onClick={() =>
            handleValidationClick(
              devDeploymentId,
              'dev',
              devValidationDeployment?.version,
              devValidationDeployment?.createdAt,
              pipeline.devValidation.validationMissing,
            )
          }
        />

        {renderDeployStep('prod')}

        <PipelineArrow
          label='Prod Deployment'
          status={pipeline.prodDeployment.status}
          version={pipeline.prodDeployment.version}
          date={pipeline.prodDeployment.date}
          disabledReason={pipeline.prodDeployment.disabledReason}
          className='-ml-3'
          clickable={prodDeploymentClickable}
          onClick={() => handleDeploymentClick(prodDeployment)}
        />

        <PipelineArrow
          label='Prod Validation'
          status={pipeline.prodValidation.status}
          version={pipeline.prodValidation.version}
          date={pipeline.prodValidation.date}
          disabledReason={pipeline.prodValidation.disabledReason}
          className='-ml-3'
          clickable={prodValidationClickable}
          onClick={() =>
            handleValidationClick(
              prodDeploymentId,
              'prod',
              prodValidationDeployment?.version,
              prodValidationDeployment?.createdAt,
              pipeline.prodValidation.validationMissing,
            )
          }
        />

        {/* Prod Changes Indicator */}
        <ChangesIndicator
          hasChanges={pipeline.prodDeployment.hasChanges}
          planOutput={pipeline.prodDeployment.planOutput}
          environment='prod'
          appName={pipeline.appName}
          hasPlan={!!prodPlanForVersion}
          onClick={() => handlePlanClick(prodPlanForVersion)}
        />
      </div>

      {/* Validation Dialog */}
      {selectedValidation &&
        (() => {
          const verification = verificationResults.find(
            (v) => v.deploymentId === selectedValidation.deploymentId,
          );
          return (
            <ValidationDialog
              open={validationDialogOpen}
              onOpenChange={setValidationDialogOpen}
              deploymentId={selectedValidation.deploymentId}
              appName={pipeline.appName}
              environment={selectedValidation.environment}
              version={selectedValidation.version}
              deployedAt={selectedValidation.deployedAt}
              verification={verification}
              validationInfo={app.validationInstructions?.[selectedValidation.environment]?.info}
              triggerUrls={app.validationInstructions?.[selectedValidation.environment]?.trigger}
              logsUrls={app.validationInstructions?.[selectedValidation.environment]?.logs}
              onValidate={handleValidate}
            />
          );
        })()}

      {/* Deployment Dialog */}
      {selectedDeployment && (
        <DeploymentDialog
          open={deploymentDialogOpen}
          onOpenChange={setDeploymentDialogOpen}
          deployment={selectedDeployment}
          isQueued={isQueued(selectedDeployment.environment)}
          onEnqueueDeployment={onEnqueueDeployment}
          onDequeueDeployment={onDequeueDeployment}
        />
      )}

      {/* Plan Dialog */}
      {selectedPlan && (
        <PlanDialog
          open={planDialogOpen}
          onOpenChange={setPlanDialogOpen}
          plan={selectedPlan}
          enabledEnvironments={enabledEnvironments}
          queuedEnvironments={queuedDeployments
            .filter((item) => item.appName === pipeline.appName)
            .map((item) => item.environment)}
          onEnqueueDeployment={onEnqueueDeployment}
          onDequeueDeployment={(environment) => onDequeueDeployment(pipeline.appName, environment)}
        />
      )}

      {/* History Dialog */}
      <HistoryDialog
        open={historyDialogOpen}
        onOpenChange={setHistoryDialogOpen}
        appName={pipeline.appName}
        deployments={deployments}
        planResults={planResults}
        verificationResults={verificationResults}
      />

      {/* Edit App Dialog */}
      <AppEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        app={app}
        onSave={onUpdateApp}
      />
    </div>
  );
};
